/**
 * <hero-slider> — Figma 154:30.
 *
 * A custom element, not an inline <script>: the Section Rendering API re-injects section HTML on
 * every theme-editor change and never executes re-inserted script tags, but custom elements upgrade
 * automatically on insertion. Same reason <product-rail> is one.
 *
 * The scrolling itself is native (overflow-x + scroll-snap), so swipe, trackpad, keyboard and
 * scroll-into-view all work with no JS at all. This class only drives the arrows, the dots, autoplay
 * and the pause control, and retints the nav per slide.
 *
 * WCAG 2.2.2 (Level A): anything that moves for more than five seconds needs a pause control, and
 * hover-pause explicitly does not count. So `paused` is a real user-owned state, separate from
 * `suspended` (hover / focus / tab hidden / scrolled away), which only defers the timer.
 */
// Wrapped in an IIFE because a section can appear twice on one page (rail.liquid does),
// which emits this script twice; a second top-level `class` declaration is a hard error.
(() => {
class HeroSlider extends HTMLElement {
  connectedCallback() {
    this.rail = this.querySelector('[data-hero-rail]');
    if (!this.rail) return;

    this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-hero-dot]'));
    this.pauseBtn = this.querySelector('[data-hero-pause]');
    // Per-slide background videos (block.settings.background_video). The pause control used to claim to
    // stop the slideshow while these kept playing underneath it: sync() only toggled `is-frozen`, which
    // freezes the dot's fill animation and nothing else. A pause button that does not stop the moving
    // thing does not satisfy WCAG 2.2.2.
    //
    // KNOWN GAP: the whole controls block, pause included, is gated on `blocks.size > 1` in the liquid.
    // A single slide carrying a video therefore autoplays motion with no control at all. Not reachable
    // today (one slide, no video configured); if a video is ever added to a one-slide hero, render
    // snippets/ambient-pause.liquid against it rather than un-gating the arrows and dots, which are
    // meaningless with one slide.
    this.videos = Array.from(this.querySelectorAll('.hero__video video'));
    this.index = 0;

    this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.wantsAutoplay = this.hasAttribute('data-autoplay') && this.slides.length > 1;

    // Autoplay is driven by the current dot's fill animation (see quote-slider). The `animationend`
    // advances; freezing the fill (`is-frozen`) freezes the countdown AND defers the advance in one
    // move, so hover holds the bar in place instead of snapping it to the end (the old bug, from
    // removing the animation and falling back to its static full-width base state).
    this.userPaused = this.motion.matches;   // reduced motion starts paused; the control still works
    this.hovered = false;
    this.focused = false;
    this.offscreen = true;   // assume off-screen until observed, so it does not run before it is seen
    // NEVER name this flag `hidden`: on an HTMLElement, `this.hidden` IS the DOM property backing the
    // `hidden` attribute, so `this.hidden = document.hidden` stamps [hidden] on the section and the UA
    // rule `[hidden] { display: none }` hides the whole hero. A page opened in a background tab (cmd+
    // click, session restore, prefetch/prerender) loads with document.hidden === true, so the hero
    // vanished until the tab was focused, then popped in -- a real CLS hit on the LCP element.
    this.tabHidden = document.hidden;

    this.querySelector('[data-hero-prev]')?.addEventListener('click', () => this.step(-1));
    this.querySelector('[data-hero-next]')?.addEventListener('click', () => this.step(1));
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i)));
    this.pauseBtn?.addEventListener('click', () => this.togglePause());

    // Hover / keyboard focus only freeze the countdown; they never flip the user's pause choice.
    // Only KEYBOARD focus (:focus-visible) freezes -- a mouse click on an arrow/dot leaves the button
    // focused but should not stop autoplay (that was the "autoplay stops after changing slide" bug).
    const keyboardFocused = () => {
      const el = document.activeElement;
      if (!el || !this.contains(el)) return false;
      try { return el.matches(':focus-visible'); } catch (e) { return false; }
    };
    this.addEventListener('pointerenter', () => this.setFlag('hovered', true));
    this.addEventListener('pointerleave', () => this.setFlag('hovered', false));
    this.addEventListener('focusin', () => this.setFlag('focused', keyboardFocused()));
    this.addEventListener('focusout', () => {
      if (!this.contains(document.activeElement)) this.setFlag('focused', false);
    });

    this.onVisibility = () => this.setFlag('tabHidden', document.hidden);
    document.addEventListener('visibilitychange', this.onVisibility);

    this.onFillEnd = (e) => {
      if (e.animationName === 'snapDotFill' && this.playing) this.step(1);
    };
    this.addEventListener('animationend', this.onFillEnd);

    // Native scroll is the source of truth for which slide is current, so a swipe updates the dots
    // and the nav tint exactly like an arrow press does.
    this.spy = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const i = this.slides.indexOf(entry.target);
          if (i > -1 && i !== this.index) this.activate(i);
        });
      },
      { root: this.rail, threshold: 0.6 }
    );
    this.slides.forEach((slide) => this.spy.observe(slide));

    // Do not run the countdown for a hero nobody is looking at.
    this.viewport = new IntersectionObserver(
      ([entry]) => this.setFlag('offscreen', !entry.isIntersecting),
      { threshold: 0.2 }
    );
    this.viewport.observe(this);

    this.onMotionChange = () => {
      if (this.motion.matches) this.setPaused(true);
    };
    this.motion.addEventListener('change', this.onMotionChange);

    this.classList.toggle('has-autoplay', this.wantsAutoplay);
    this.activate(0);
    this.syncPauseButton();
    this.sync();
  }

  disconnectedCallback() {
    this.spy?.disconnect();
    this.viewport?.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.motion?.removeEventListener('change', this.onMotionChange);
    this.removeEventListener('animationend', this.onFillEnd);
    document.documentElement.style.removeProperty('--hero-nav-ink');
    document.body.classList.remove('hero-logo-invert');
  }

  /** The slide that is current owns the nav tint, and cues its own copy in. */
  activate(i) {
    this.index = i;
    const slide = this.slides[i];

    // Removing and re-adding the attribute restarts the staggered entrance in CSS.
    this.slides.forEach((s, n) => s.toggleAttribute('data-current', n === i));

    this.dots.forEach((dot, n) => {
      if (n === i) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });

    document.documentElement.style.setProperty('--hero-nav-ink', slide.dataset.navInk || '#fbfaf6');
    document.body.classList.toggle('hero-logo-invert', slide.dataset.logoInvert === 'true');

    this.restartProgress();
  }

  /** Re-run the dot's fill animation from zero. Reading offsetWidth forces the reflow that makes the
   *  removal take effect before the class is put back. */
  restartProgress() {
    const fill = this.dots[this.index]?.querySelector('[data-hero-fill]');
    if (!fill) return;
    fill.style.animation = 'none';
    void fill.offsetWidth;
    fill.style.animation = '';
  }

  go(i) {
    const count = this.slides.length;
    const next = ((i % count) + count) % count;
    this.rail.scrollTo({
      left: this.slides[next].offsetLeft - this.slides[0].offsetLeft,
      behavior: this.motion.matches ? 'auto' : 'smooth',
    });
    this.activate(next);
  }

  step(delta) {
    this.go(this.index + delta);
  }

  togglePause() {
    this.setPaused(!this.userPaused);
  }

  setPaused(paused) {
    this.userPaused = paused;
    this.syncPauseButton();
    this.sync();
    if (!paused) this.restartProgress();   // resuming starts the current slide's countdown fresh
  }

  setFlag(name, value) {
    if (this[name] === value) return;
    this[name] = value;
    this.sync();
  }

  syncPauseButton() {
    if (!this.pauseBtn) return;
    this.pauseBtn.setAttribute('aria-pressed', String(this.userPaused));
    this.pauseBtn.setAttribute('aria-label', this.userPaused ? 'Play the slideshow' : 'Pause the slideshow');
  }

  get playing() {
    return this.wantsAutoplay && !this.userPaused && !this.hovered && !this.focused
      && !this.offscreen && !this.tabHidden;
  }

  /** One place that reflects state into the DOM. `is-frozen` pauses the fill animation (freezing the
   *  countdown and deferring its animationend) without removing it, so the bar holds in place. */
  sync() {
    this.classList.toggle('is-frozen', this.wantsAutoplay && !this.playing);
    this.syncVideos();
  }

  /** Slide videos follow the visitor's pause choice, not the rotation countdown. No-op with no videos. */
  syncVideos() {
    if (!this.videos.length) return;
    this.videos.forEach((video) => {
      // Deliberately NOT gated on `playing`: hover and keyboard focus freeze the countdown so the
      // visitor can read the slide, but stopping the film because the pointer entered the hero would
      // be a different (and worse) behaviour. userPaused is the explicit choice; the rest is presence.
      const onActiveSlide = !!this.slides[this.index]?.contains(video);
      const shouldPlay = onActiveSlide && !this.userPaused && !this.offscreen
        && !this.tabHidden && !this.motion.matches;
      if (shouldPlay) video.play?.().catch(() => {});
      else video.pause?.();
    });
  }
}

if (!customElements.get('hero-slider')) customElements.define('hero-slider', HeroSlider);
})();
